import { TrackingEvents } from "@/pages/SetupWorkflow/types";
import { setEventTracking } from "@/utils";
import { Tooltip } from "@flash-tecnologia/hros-web-ui-v2";
import { useGetSetup } from "@hooks/use-get-setup";
import { useSession } from "@hooks/use-session";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ProgressBar } from "src/components/core-components";
import {
  CollapsedContainer,
  Container,
  Content,
  InfoShapeIcon,
  Title,
  TypographyCaption2,
} from "./setup-card.styles";

interface SetupCardProps {
  isCollapsed?: boolean;
}

export const SetupCard = ({ isCollapsed }: SetupCardProps) => {
  const navigate = useNavigate();
  const { companyId } = useSession();
  const { setup, isLoading, error } = useGetSetup(companyId);

  const { currentStepIndex, totalSteps, progress } = useMemo(() => {
    if (!setup) return { currentStepIndex: 0, totalSteps: 0, progress: 0 };

    const steps = Array.isArray(setup.baseSteps) ? setup.baseSteps : [];
    const total = steps.length;
    const pendingIndex = steps.findIndex((step) =>
      ["PENDING", "DOING"].includes(step.status)
    );
    const completedCount = pendingIndex >= 0 ? pendingIndex : total;
    const progressPercent =
      total === 0 ? 0 : Math.round((completedCount / total) * 100);

    return {
      currentStepIndex:
        pendingIndex >= 0 ? pendingIndex : Math.max(0, total - 1),
      totalSteps: total,
      progress: progressPercent,
    };
  }, [setup]);

  if (!setup || isLoading || error) return null;

  const onCardClick = () => {
    setEventTracking(TrackingEvents.SETUP_MENU_CLICKED);

    const currentStep = setup.baseSteps[currentStepIndex];
    const redirectPath =
      currentStep.type === "BASE"
        ? `/setup/${setup.id}/workflows/${setup.baseSteps[currentStepIndex].externalId}`
        : currentStep.redirectPath;

    navigate(`${redirectPath}?company=${companyId}`);
  };

  if (setup.status === "COMPLETED") {
    return null;
  }

  if (isCollapsed) {
    return (
      <CollapsedContainer onClick={onCardClick} role="button" tabIndex={0}>
        <InfoShapeIcon
          name="IconPlayerPlay"
          size={24}
          variant="info"
          fill="info"
        />
      </CollapsedContainer>
    );
  }

  return (
    <Container onClick={onCardClick} role="button" tabIndex={0}>
      <InfoShapeIcon
        name="IconPlayerPlay"
        size={24}
        variant="info"
        fill="info"
      />
      <Content isCollapsed={isCollapsed}>
        <Title variant="headline10" color="neutral.30">
          Comece a usar a Flash
        </Title>
        <Tooltip
          arrow
          placement="right"
          title={setup.baseSteps[currentStepIndex]?.title}
        >
          <TypographyCaption2 color="neutral.30" as="span" variant="caption">
            Etapa {currentStepIndex + 1}/{totalSteps}:{" "}
            {setup.baseSteps[currentStepIndex]?.title}
          </TypographyCaption2>
        </Tooltip>
        <ProgressBar progress={progress} variant="info" animationTime={800} />
      </Content>
    </Container>
  );
};
